/* ─────────────────────────────────────────────────────────────────
   PYTHON PROGRAMMING — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const PYTHON_MODULES = [
  {
    module: 1,
    title: "Python Basics",
    emoji: "🐍",
    topics: [
      {
        id: 1,
        topic: "Python Syntax & Variables",
        description: "Learn Python's core syntax, variables, data types, and basic input/output — the building blocks of every Python program.",
        motivation: "Great start! Every Python expert once wrote their very first print statement — you just did too. 🐍",
        resource: {
          name: "Python Official Docs — An Informal Introduction",
          url: "https://docs.python.org/3/tutorial/introduction.html",
        },
        task: "Build a command-line BMI (Body Mass Index) Calculator.",
        requirements: [
          "Take height (in meters) and weight (in kg) as input from the user",
          "Calculate BMI using the correct formula",
          "Print the BMI value rounded to 2 decimal places",
          "Classify the result as Underweight, Normal, Overweight, or Obese",
          "Handle invalid input gracefully (e.g. text instead of numbers)",
          "Use clear variable names and comments explaining each step",
        ],
      },
      {
        id: 2,
        topic: "Control Flow — Conditionals & Loops",
        description: "Learn how to control program flow using if/elif/else statements, for loops, and while loops.",
        motivation: "Loops and conditions mastered! You can now make programs that think and repeat — real programming logic. 🔁",
        resource: {
          name: "Python Official Docs — More Control Flow Tools",
          url: "https://docs.python.org/3/tutorial/controlflow.html",
        },
        task: "Build a Number Guessing Game.",
        requirements: [
          "Program randomly picks a number between 1 and 100",
          "User keeps guessing until they get it right",
          "Give 'Too High' / 'Too Low' hints after each guess",
          "Count and display the number of attempts taken",
          "Ask if the user wants to play again after winning",
          "Use a while loop for the main game loop",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "Data Structures & Functions",
    emoji: "🧩",
    topics: [
      {
        id: 3,
        topic: "Lists, Tuples & Dictionaries",
        description: "Learn Python's core data structures and how to store, access, and manipulate collections of data.",
        motivation: "Data structures unlocked! Lists, tuples, and dictionaries are what real Python apps are built on. 📦",
        resource: {
          name: "Python Official Docs — Data Structures",
          url: "https://docs.python.org/3/tutorial/datastructures.html",
        },
        task: "Build a Student Grade Management System (console-based).",
        requirements: [
          "Store multiple students as a dictionary (name → list of subject scores)",
          "Add a new student with their scores",
          "Calculate and display each student's average score",
          "Find and display the topper (highest average)",
          "Display all students sorted by average score",
          "Use at least one list, one tuple, and one dictionary meaningfully",
        ],
      },
      {
        id: 4,
        topic: "Functions & Modules",
        description: "Learn to write reusable functions and organize code across multiple modules/files.",
        motivation: "You're writing reusable, organized code now — that's the difference between a script and real software. 🛠️",
        resource: {
          name: "Python Official Docs — Defining Functions",
          url: "https://docs.python.org/3/tutorial/controlflow.html#defining-functions",
        },
        task: "Build a Simple Calculator library split across multiple files (modules).",
        requirements: [
          "Create a separate module file (e.g. operations.py) with add, subtract, multiply, divide functions",
          "Import and use these functions from a main.py file",
          "Use default arguments and keyword arguments in at least one function",
          "Handle division-by-zero using a function-level check",
          "Include docstrings explaining what each function does",
          "Print a formatted menu so the user can choose an operation",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "OOP & File Handling",
    emoji: "🗂️",
    topics: [
      {
        id: 5,
        topic: "Object-Oriented Programming in Python",
        description: "Learn classes, objects, inheritance, and encapsulation to structure larger Python applications.",
        motivation: "OOP mastered! You now think in classes and objects — this is how professional Python applications are designed. 🏗️",
        resource: {
          name: "Python Official Docs — Classes",
          url: "https://docs.python.org/3/tutorial/classes.html",
        },
        task: "Build a Bank Account Management System using classes.",
        requirements: [
          "Create a base class BankAccount with balance, deposit(), withdraw() methods",
          "Create a SavingsAccount subclass that adds interest calculation",
          "Prevent overdrawing the balance (raise a clear error/message)",
          "Maintain a transaction history list per account",
          "Create at least 2 account objects and demonstrate all methods",
          "Use encapsulation — keep balance private-ish (prefix with _ or __)",
        ],
      },
      {
        id: 6,
        topic: "File Handling & Exception Handling",
        description: "Learn to read/write files and handle errors gracefully using try/except blocks.",
        motivation: "Your programs survive real-world messiness now — file I/O and error handling are core professional skills. 📄",
        resource: {
          name: "Python Official Docs — Errors and Exceptions",
          url: "https://docs.python.org/3/tutorial/errors.html",
        },
        task: "Build a Contact Book application that saves data to a file.",
        requirements: [
          "Add, view, search, and delete contacts (name, phone, email)",
          "Persist contacts to a .txt or .json file so data survives restarts",
          "Load existing contacts from the file on startup",
          "Handle a missing file gracefully on first run (auto-create it)",
          "Wrap file operations in try/except to handle errors cleanly",
          "Validate phone number and email format before saving",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Command-Line Expense Tracker",
        description: "Apply everything learned throughout the MicroIntern to build a complete, real-world Python application.",
        motivation: "Incredible work! You built a complete, persistent Python application from scratch. That's certificate-worthy. 🎉",
        resource: {
          name: "Python Official Docs — Tutorial Index",
          url: "https://docs.python.org/3/tutorial/index.html",
        },
        task: "Build a full Command-Line Expense Tracker application.",
        requirements: [
          "Add an expense with amount, category, and date",
          "View all expenses in a readable table format",
          "Filter expenses by category or date range",
          "Show total spending and a category-wise breakdown",
          "Persist all data to a JSON or CSV file",
          "Use functions and at least one class to organize the code",
          "Handle invalid input and file errors gracefully",
          "Include a clean, menu-driven interface (loops until user exits)",
          "Add a README.md explaining how to run the project",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const PYTHON_TASKS = PYTHON_MODULES.flatMap((m) => m.topics);
